'use client';

import { useState } from 'react';
import { Activity, ChevronDown, ChevronUp, Loader2, X, XCircle } from 'lucide-react';
import { useIndexingStore } from '@/store/indexingStore';
import { isDrainedEntry, useQuestionnaireRunStore, type RunPhase } from '@/store/questionnaireRunStore';
import { useNamespacedTranslate } from '@/shared/lib/locales';
import { useSmoothProgress } from '@/shared/hooks/useSmoothProgress';
import { useModal } from '@/shared/providers/ModalProvider';

interface ProgressEntry {
  key: string;
  projectId: string;
  /** Chave do processo dentro do projeto (ver `runKey` no store) — o âmbito
   * do dismiss desta entrada. Só nas entradas de execução. */
  runKey?: string;
  type: 'indexing' | 'run';
  status: 'running' | 'error';
  phase?: RunPhase;
  projectName: string;
  questionnaireName?: string;
  /** Questionário a que esta entrada diz respeito, quando é um só (null numa
   * execução de "todos") — define o âmbito do cancelamento. */
  questionnaireId?: string | null;
  progress: number;
  message: string | null;
  error?: string | null;
  isCancelling: boolean;
  cancelError: string | null;
}

export function ProgressToast() {
  const t = useNamespacedTranslate('progressToast');
  const { confirm } = useModal();
  const [isMinimized, setIsMinimized] = useState(false);
  const indexingByProject = useIndexingStore((s) => s.projectStatusById);
  const cancelProjectIndexing = useIndexingStore((s) => s.cancelProjectIndexing);
  const runByProject = useQuestionnaireRunStore((s) => s.projectStatusById);
  const cancelRun = useQuestionnaireRunStore((s) => s.cancelRun);
  const dismissRun = useQuestionnaireRunStore((s) => s.dismissRun);

  const handleEntryAction = async (entry: ProgressEntry) => {
    if (entry.isCancelling) return;
    const isError = entry.type === 'run' && entry.status === 'error';
    if (entry.type === 'indexing') return cancelProjectIndexing(entry.projectId);
    if (isError) return dismissRun(entry.projectId, entry.runKey);
    // Confirmação obrigatória: este é o mesmo X que fecha a entrada quando há
    // erro, e cancelar por engano uma execução a meio custa ao utilizador todo
    // o tempo que falta voltar a gerar.
    const confirmed = await confirm({
      title: t('cancelRunConfirmTitle'),
      message: t('cancelRunConfirmMessage'),
      confirmLabel: t('cancelRunConfirmLabel'),
      cancelLabel: t('cancelRunKeepRunningLabel'),
      variant: 'danger',
    });
    if (!confirmed) return;
    return cancelRun(entry.projectId, entry.questionnaireId);
  };

  const entries: ProgressEntry[] = [];

  for (const [projectId, state] of Object.entries(indexingByProject)) {
    if (state.isIndexing) {
      entries.push({
        key: `indexing-${projectId}`,
        projectId,
        type: 'indexing',
        status: 'running',
        projectName: state.projectName ?? projectId,
        progress: state.progress_percent,
        message: state.current_document ?? state.status,
        isCancelling: state.isCancelling,
        cancelError: state.cancelError,
      });
    }
  }

  // Uma entrada por processo em curso, e não uma por projeto: as duas fases
  // de uma execução (responder às perguntas e calcular a confiança), tal
  // como questionários diferentes de uma execução de "todos", correm ao
  // mesmo tempo e têm cada uma a sua barra — ver `runs` no store.
  //
  // A conclusão de um questionário é comunicada por um banner na própria página do projeto,
  // não neste popup — só o estado de erro (que não tem outro sítio de destino) continua aqui.
  // `completed_partial` (execução interrompida a meio) também tem banner
  // próprio na página, e mostrá-la aqui daria uma entrada "a responder" a
  // 100% para uma execução que já parou.
  for (const [projectId, state] of Object.entries(runByProject)) {
    let hasRunning = false;
    for (const [key, run] of Object.entries(state.runs)) {
      // Fase sem trabalho aberto à espera da confirmação do desfecho: já não
      // há processo nenhum a mostrar (ver `isDrainedEntry`). É o que impede
      // uma barra a mais — uma fase que acabou, ou cuja mensagem de
      // conclusão se perdeu, ficava aqui a girar ao lado das barras reais.
      if (isDrainedEntry(run)) continue;
      if (run.status === 'running') hasRunning = true;
      if (run.status !== 'running' && run.status !== 'error') continue;
      entries.push({
        key: `run-${projectId}-${key}`,
        projectId,
        runKey: key,
        type: 'run',
        status: run.status === 'error' ? 'error' : 'running',
        phase: run.phase,
        projectName: state.projectName ?? projectId,
        questionnaireName: run.questionnaireName,
        questionnaireId: run.questionnaireId,
        progress: run.progress,
        message: run.message,
        error: run.error,
        isCancelling: run.isCancelling,
        cancelError: run.cancelError,
      });
    }
    // Execução de "todos" pedida mas ainda sem nenhum processo a reportar
    // progresso (ou entre um questionário e o seguinte): mantém uma entrada
    // agregada para o popup não desaparecer no intervalo.
    if (state.isRunningAll && !hasRunning) {
      entries.push({
        key: `run-${projectId}-all`,
        projectId,
        type: 'run',
        status: 'running',
        phase: 'answering',
        projectName: state.projectName ?? projectId,
        questionnaireId: null,
        progress: 0,
        message: null,
        isCancelling: false,
        cancelError: null,
      });
    }
  }

  if (entries.length === 0) return null;

  const hasUnseenError =
    isMinimized &&
    entries.some((entry) => (entry.type === 'run' && entry.status === 'error') || entry.cancelError);

  return (
    <div className={`fixed bottom-5 right-5 z-50 ${isMinimized ? 'w-fit' : 'w-80'}`}>
      <div className="bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div
          className={`flex items-center gap-2 px-4 py-2.5 bg-gray-50 ${
            isMinimized ? '' : 'border-b border-gray-100'
          } ${hasUnseenError ? 'animate-pulse' : ''}`}
        >
          <Activity className="h-3.5 w-3.5 text-gray-400 animate-pulse shrink-0" />
          <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
            {entries.length === 1
              ? t('operationsSingular')
              : t('operationsPlural', { count: entries.length })}
          </span>
          {hasUnseenError && <span className="h-1.5 w-1.5 rounded-full shrink-0 bg-red-500" />}
          <button
            onClick={() => setIsMinimized((prev) => !prev)}
            className="ml-auto text-gray-400 hover:text-gray-600 transition-colors rounded p-0.5 shrink-0"
            title={isMinimized ? t('restoreTitle') : t('minimizeTitle')}
          >
            {isMinimized ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </button>
        </div>

        {/* Entries */}
        {!isMinimized && (
          <div className="divide-y divide-gray-50">
            {entries.map((entry) => (
              <ProgressEntryRow
                key={entry.key}
                entry={entry}
                onAction={() => handleEntryAction(entry)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Uma linha do popup. É um componente à parte, e não JSX inline no `.map`,
 * porque `useSmoothProgress` guarda estado de animação por entrada: com a
 * `key` estável (ver `entry.key`) o React preserva esse estado entre
 * renders, e cada barra anima independentemente das outras.
 */
function ProgressEntryRow({ entry, onAction }: { entry: ProgressEntry; onAction: () => void }) {
  const t = useNamespacedTranslate('progressToast');
  const isError = entry.type === 'run' && entry.status === 'error';
  const isConfidence = entry.type === 'run' && entry.phase === 'confidence';
  const textColorClass =
    entry.type === 'indexing' ? 'text-yellow-600' : isConfidence ? 'text-blue-600' : 'text-green-600';
  const barColorClass =
    entry.type === 'indexing' ? 'bg-yellow-500' : isConfidence ? 'bg-blue-500' : 'bg-green-500';

  // A percentagem mostrada — barra E número — é a interpolada, não a última
  // recebida: o backend emite degraus (ver as fronteiras de fase em
  // `RAGSystemDocumentLoader`) e cala-se durante as fases mais lentas.
  const progress = useSmoothProgress(entry.progress, !isError);

  return (
    <div className={`px-4 py-3 ${isError ? 'bg-red-50 border-l-4 border-red-400' : ''}`}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-[11px] font-medium mb-0.5 truncate inline-flex items-center gap-1">
            {isError && <XCircle className="h-3 w-3 text-red-600 shrink-0" />}
            <span className={isError ? 'text-red-600' : textColorClass}>
              {isError
                ? t('failed')
                : entry.type === 'indexing'
                  ? t('indexingLabel')
                  : isConfidence
                    ? t('confidenceLabel')
                    : t('runningLabel')}{' '}
              -
            </span>
            <span className="font-semibold text-gray-900">{entry.projectName}</span>
            {entry.questionnaireName && (
              <span className="font-normal text-gray-500">
                {' · '}
                {entry.questionnaireName}
              </span>
            )}
          </span>
          {!isError && entry.message && (
            <span className="text-[11px] text-gray-400 truncate">{entry.message}</span>
          )}
          {isError && entry.error && (
            <span className="text-[11px] text-red-500 truncate">{entry.error}</span>
          )}
          {!isError && entry.cancelError && (
            <span className="text-[11px] text-red-500 truncate">
              {t('cancelFailedError', { error: entry.cancelError })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {!isError && (
            <span className={`text-xs font-semibold tabular-nums ${textColorClass}`}>
              {Math.round(progress)}%
            </span>
          )}
          <button
            onClick={onAction}
            disabled={entry.isCancelling}
            className={`text-gray-400 transition-colors rounded p-0.5 ${
              entry.isCancelling ? 'cursor-wait' : 'hover:text-red-500'
            }`}
            title={entry.isCancelling ? t('cancellingTitle') : isError ? t('dismissTitle') : t('cancelTitle')}
          >
            {entry.isCancelling ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <X className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {!isError && (
        <div className="w-full bg-gray-100 rounded-full h-1.5">
          {/* Sem `transition-*` na largura: a animação já é feita frame a
              frame por `useSmoothProgress`, e uma transição CSS por cima
              dela só acrescentaria atraso. */}
          <div
            className={`h-1.5 rounded-full ${barColorClass}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}
